/**
 * RelocateDialog — Area picker for moving a resident to a different area.
 *
 * Shared by the residents list (ResidentsPage row actions) and the resident
 * profile (ResidentProfilePage lifecycle actions).
 *
 * Areas at max occupancy are listed and searchable but not selectable, so a
 * full room is visibly present rather than appearing to be missing. Occupancy
 * comes from the area rows themselves (number_residents / max_occupancy as
 * returned by sp_dev_getareasbycampusid).
 */

import { useState, useCallback, useMemo } from 'react';
import SearchableSelect from '../../../../components/v2/SearchableSelect.jsx';

const RelocateDialog = ({
  currentAreaId, locationData,
  onClose, onRelocate, saving,
}) => {
  const [selectedAreaId, setSelectedAreaId] = useState('');

  const areaGroups = useMemo(() => {
    const byName = (a, b) =>
      (a.area_name || '').localeCompare(b.area_name || '', undefined, {
        sensitivity: 'base',
        numeric: true,
      });
    const available = [];
    const unavailable = [];
    (locationData || []).forEach((area) => {
      const max = parseInt(area.max_occupancy, 10) || 0;
      const occupied = parseInt(area.number_residents, 10) || 0;
      const isCurrent = area.area_id?.toString() === currentAreaId?.toString();
      // The resident's current room is never a valid relocate target.
      if (isCurrent || occupied >= max) unavailable.push({ area, isCurrent });
      else available.push({ area, isCurrent });
    });
    available.sort((x, y) => byName(x.area, y.area));
    unavailable.sort((x, y) => byName(x.area, y.area));

    const toOption = ({ area, isCurrent }, disabled) => ({
      value: area.area_id.toString(),
      label: area.area_name,
      hint: isCurrent ? 'Current' : disabled ? 'Full' : undefined,
      isDisabled: disabled,
      searchText: area.area_name || '',
    });

    return [
      ...(available.length
        ? [{ label: 'Available', options: available.map((a) => toOption(a, false)) }]
        : []),
      ...(unavailable.length
        ? [{ label: 'Unavailable', options: unavailable.map((a) => toOption(a, true)) }]
        : []),
    ];
  }, [locationData, currentAreaId]);

  const handleSubmit = useCallback(() => {
    if (!selectedAreaId) return;
    onRelocate(selectedAreaId);
  }, [selectedAreaId, onRelocate]);

  const isValid = selectedAreaId && selectedAreaId.toString() !== currentAreaId?.toString();

  return (
    <div className="pp-dialog-backdrop" onClick={onClose}>
      <div className="pp-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="pp-dialog__header">
          <h2 className="pp-dialog__title">Relocate Resident</h2>
          <button className="dp-drawer__close" onClick={onClose}>&#x2715;</button>
        </div>
        <div className="pp-dialog__body">
          {/* data-help-id anchors for the interactive help guide (see
              docs/INTERACTIVE_HELP_PLAN.md §5.2). This dialog is shared by
              ResidentsPage and ResidentProfilePage with the same meaning, so the
              ids are deliberately unqualified — the guide works from either
              entry point. */}
          <div className="dp-drawer__field" data-help-id="relocate-dialog.area-select">
            <label className="dp-drawer__label">
              Select New Area <span className="dp-drawer__required">*</span>
            </label>
            <SearchableSelect
              id="relocate-area"
              groups={areaGroups}
              value={selectedAreaId}
              onChange={(val) => setSelectedAreaId(val || '')}
              placeholder="Search or select area..."
              ariaLabel="New area"
              required
              isClearable
            />
          </div>
        </div>
        <div className="pp-dialog__footer">
          <button className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="dp-btn dp-btn--primary"
            data-help-id="relocate-dialog.submit"
            onClick={handleSubmit}
            disabled={saving || !isValid}
          >
            {saving ? 'Relocating\u2026' : 'Relocate'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RelocateDialog;
